<template>
  <div
    :class="[
      `form-fieldset`,
      `form-fieldset--${fieldType}`,
      {
        'form-fieldset--required': required
      },
      fieldsetClasses
    ]"
    :data-fieldset="storeId"
  >
    <p v-if="pre" :class="[`form-pre`, `form-pre--${state}`]">
      {{ pre }}
    </p>

    <!-- Fieldset wrapper -->
    <div
      :class="[
        `form-fieldset__wrapper`,
        `form-fieldset__wrapper--${fieldType}`,
        {
          'form-fieldset__wrapper--highlight-error': highlightError
        }
      ]"
    >
      <!-- Label -->
      <component
        v-if="labelTag"
        :is="labelTag"
        :class="[`form-label`, `form-label--${type}`, `form-label--${state}`]"
        :for="id"
      >
        <span class="form-label__label p">{{ label }}</span>
        <span class="form-label__required" v-if="required">Required</span>
        <span v-if="helper" class="form-label__helper small">{{ helper }}</span>
      </component>

      <span class="form-label__timezone" v-if="id === 'zip'" @click="openTimezone = true">{{
        currentTimezone || 'Eastern'
      }}</span>

      <!-- Input/Select -->
      <div
        v-if="
          fieldType === `input` ||
            fieldType === `select` ||
            fieldType === `file`
        "
        :class="[`form-field`, `form-field--${type}`, `form-field--${state}`]"
        
      >
        <!-- Dropdown Arrow (select, date) -->
        <svg
          v-if="typeSet === `date` || fieldType === `select`"
          :class="[`form-field__arrow`, `form-field__arrow--${type}`]"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <path
              d="M4.914 3.5l4.293 4.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5A1 1 0 0 1 9.207-.793L4.914 3.5z"
              id="a"
            />
          </defs>
          <use
            fill="#75726F"
            transform="matrix(0 -1 -1 0 9.5 9.5)"
            xlink:href="#a"
            fill-rule="evenodd"
          />
        </svg>

        <!-- File upload -->
        <template v-if="typeSet === `file`">
          <module-button
            :class="[`form-file__button`]"
            type="button"
            label="Attach Files"
            size="s"
            @click.native="removeValue"
          />
        </template>

        <!-- Password Toggle (password) -->
        <module-link
          v-if="$route.name === `login` && type === `password`"
          :class="[
            `form-field__toggle-password`,
            {
              'form-field__toggle-password--active': typeOverride
            }
          ]"
          label="Forgot?"
          title="Forgot?"
          type="button"
          link="auth-reset"
        />

        <!-- Tel prefix -->
        <span v-if="type === `tel`" class="form-tel-prefix">+1</span>

        <!-- Textarea -->
        <textarea
          v-if="typeSet === `textarea`"
          :class="[`form-input`, `form-input--textarea`]"
          v-bind="inputAttrs"
          ref="input"
          @change="onChange"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        />

        <!-- Text Input -->
        <input
          v-else-if="fieldType === `input`"
          :pattern="pattern"
          :class="[
            `form-input`,
            `form-input--${typeSet}`,
            `form-input--${state}`,
            {
              'form-input--file-filled': typeSet === `file` && filled
            }
          ]"
          v-bind="inputAttrs"
          ref="input"
          @change="onChange"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        >

        <!-- Select -->
        <div
          v-else-if="fieldType === `select`"
          :class="[`form-input`, `form-input--${type}`]"
        >
          <select
            :class="[
              `form-select`,
              {
                'form-select--placeholder': !filled
              }
            ]"
            v-bind="inputAttrs"
            ref="input"
            @focus="onFocus"
            @change="onInput"
            @blur="onBlur"
          >
            <option
              disabled="true"
              value="-1"
              v-html="`${placeholder || `Select...`}`"
            />
            <option
              v-for="(option, index) in options"
              :key="index"
              :value="`${option.id ? option.id : index}`"
              v-html="option.label || option.location || option"
            />
          </select>
        </div>
      </div>
      <span v-if="helper && typeSet === `textarea`" class="form-label__helper small">{{ helper }}</span>
      <!-- Toggles – Checkbox, Radio, Boolean -->
      <template v-else-if="toggles !== null">
        <div
          v-for="(toggle, index) in toggles"
          :key="index"
          :class="[
            `form-toggle`,
            `form-toggle--${fieldType}`,
            {
              'form-toggle--checked':
                (fieldType === `boolean` && fieldView) ||
                (fieldType === `radio` && fieldModel === `${index}`) ||
                (fieldType === `checkbox` && (fieldModel || {})[index])
            }
          ]"
        >
          <span
            :class="[
              `form-toggle__toggle`,
              `form-toggle__toggle--${fieldType}`
            ]"
          >
            <module-icon v-if="fieldType === `boolean`" icon="tick" />
          </span>

          <label
            :class="[
              `form-toggle__label`,
              `form-toggle__label--${fieldType}`,
              fieldType === `boolean` ? `p` : `small`
            ]"
            :for="`${id}-${index}`"
            v-html="toggle.label"
          />
          <input
            :name="id"
            :id="`${id}-${index}`"
            :type="fieldType === `boolean` ? `checkbox` : fieldType"
            :value="fieldType === `boolean` ? true : `${index}`"
            ref="input"
            @change="onChange"
          >
        </div>
      </template>
    </div>

    <!-- Preview -->
    <div v-if="typeSet === `file` && preview" class="form-preview">
      <component
        v-if="previewData.src"
        :is="previewData.is || `img`"
        class="form-preview__preview"
        :src="previewData.src"
      />
      <span
        v-else
        class="form-preview__label"
        v-html="filled ? `Preview unavailable` : `Upload a file to see preview`"
      />
    </div>

    <module-modal
      :modal="modalTimezoneData"
      @close="
        openTimezone = false;
        tempTimezone = '';
      "
      v-show="openTimezone"
    >
      <template slot="modal-body">
        <div class="form results__state">
          <div class="form-wrap">
            <div class="form-fieldset">
              <label class="form-label">Your Time Zone</label>
              <div class="form-field">
                <div class="form-wrap">
                  <select
                    :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                    class="form-select"
                    @change="tempTimezone = $event.target.value"
                  >
                    <option
                      :disabled="true"
                      :selected="!currentTimezone || !tempTimezone"
                      value="America/New_York"
                    >
                      Eastern
                    </option>
                    <option
                      v-for="choice in timezones"
                      :value="choice.value"
                      :key="choice.value"
                      :selected="choice.value === timezone"
                      :disabled="choice.value === timezone"
                    >
                      {{ choice.label }}
                    </option>
                  </select>
                  <span class="form-arrow" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template slot="modal-footer">
        <module-button
          :disabled="tempTimezone && tempTimezone.length <= 0"
          type="button"
          @click.native="handleTimezoneSelect(tempTimezone)"
        >Update</module-button
        >
        <module-button
          type="button"
          @click.native="
            openTimezone = false;
            tempTimezone = '';
          "
        >Cancel</module-button
        >
      </template>
    </module-modal>

    <!-- Field Error -->
    <module-error :field="field" :id="storeId" :hide="!showError" />
    <template v-if="typeSet === `file`">
      <div class="mt">
        <div
          class="mt-tiny "
          v-for="(file, key) in getFiles"
          :key="key + `files`"
        >
          <span class="c-accent">
            <module-icon icon="document-upload" />
            {{ file.name }}
            <module-icon
              icon="close"
              @click.native="removeFile(key)"
              class="cp c-accent"
            />
          </span>
        </div>
      </div>
    </template>
    <span v-if="footNote" class="p form-fieldset__foot-note">{{
      footNote
    }}</span>
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex';

// Utils
import { arrayIncludes, arrayLoop } from 'utils/array';

import moment from 'moment';

// Models
import countries from 'models/contact/countries';
import timezones from 'models/auth/timezones';

// Transitions
import TransitionExpandHeight from 'components/ExpandHeight';
import ModuleModal from 'components/Modal';

// Export
export default {
  name: 'module-fieldset',

  // Components
  components: {
    TransitionExpandHeight,
    ModuleModal,
  },

  // Props
  props: {
    id: {
      type: String,
      required: true,
    },
    fieldsetClasses: {
      type: String,
    },
    pre: {
      type: String,
    },
    label: {
      type: String,
      required: true,
    },
    helper: {
      type: String,
    },
    value: {
      type: [String, Number, Boolean, Object],
      default: () => undefined,
    },
    options: {
      type: Array,
    },
    preview: {
      type: Boolean,
    },
    footNote: {
      type: String,
    },
    pattern: {
      type: String,
    },

    // Field attributes
    placeholder: {
      type: String,
    },
    type: {
      type: String,
      default: 'text',
    },
    autocomplete: {
      type: String,
    },
    readonly: {
      type: Boolean,
    },

    // Validation props
    required: {
      type: Boolean,
    },
    min: {
      type: Number,
      default: () => undefined,
    },
    max: {
      type: Number,
      default: () => undefined,
    },
    matches: {
      type: String,
      default: () => undefined,
    },
    accept: {
      type: [Array, String],
      default: () => undefined,
    },
  },

  // Data
  data() {
    return {
      countries,
      valueSet: undefined,
      parentId: '',
      focus: false,
      typeOverride: '',
      previewData: {
        src: '',
      },
      highlightError: false,
      timezones,
      timezone: '',
      tempTimezone: '',
      openTimezone: false,
      modalTimezoneData: {
        title: 'Update your Time Zone',
      },
    };
  },
  computed: {
    ...mapGetters('forms', [
      'separator',
      'browserSupportsInputDate',
      'getField',
      'getFieldTelPrefix',
      'getFieldDirty',
      'getForm',
      'getFiles',
    ]),
    ...mapGetters('app', ['org']),
    labelTag() {
      const fieldType = this.fieldType;

      if (fieldType === 'boolean') return '';
      if (this.toggles !== null) return 'span';

      return 'label';
    },
    currentTimezone() {
      if (this.timezone && this.timezone.length > 0) {
        return timezones.filter(tz => tz.value === this.timezone)[0].label;
      }
    },
    toggles() {
      let toggles = null;

      if (this.fieldType === 'boolean') {
        toggles = [
          {
            label: this.label,
            value: true,
          },
        ];
      }

      if (this.fieldType === 'checkbox' || this.fieldType === 'radio') {
        toggles = this.options || [];
      }

      return toggles;
    },
    field() {
      return this.getField(this.storeId) || {};
    },
    storeId() {
      return `${this.parentId}|${this.id}`; // TODO: Var separator
    },
    minSet() {

      if (this.type === 'password' && !this.min) return 8;
      if (this.type === 'date' && this.id === 'appointment_date') return moment().add('2','days').format('YYYY-MM-DD');

      return this.min;
    },
    inputAttrs() {
      const attrs = {
        id: this.id,
        placeholder: this.placeholderSet,
        type: this.typeOverride || this.type,
        min: this.minSet,
        max: this.max,
        autocomplete: this.autocomplete,
        readonly: this.readonly || this.formState === 'loading',
        multiple: this.multiple,
      };

      if (this.acceptSet) attrs.accept = this.acceptSet;
      if (arrayIncludes(['url', 'number'], this.typeSet))
        attrs.novalidate = 'novalidate';

      return attrs;
    },
    fieldType() {
      const input = [
        'text',
        'url',
        'email',
        'number',
        'date',
        'password',
        'textarea',
        'file',
        'tel',
      ];

      return arrayIncludes(input, this.type) ? 'input' : this.type;
    },
    fieldView() {
      return this.field.view;
    },
    fieldModel() {
      return this.field.model;
    },
    dirty() {
      return this.getFieldDirty(this.storeId);
    },
    showError() {
      return (
        (this.field.serverError || this.field.error) &&
        this.dirty &&
        !this.readonly
      );
    },
    filled() {
      if (this.typeSet === 'select' && this.fieldModel)
        return this.fieldModel !== '-1';
      if (this.fieldType === 'input' && this.typeSet !== 'number')
        return this.fieldModel;

      return typeof this.fieldModel !== 'undefined';
    },
    state() {
      if (this.readonly) return 'readonly';
      if (this.focus) return 'focus';
      if (this.showError) return 'error';
      if (this.formState === 'loading') return 'loading';
      if (this.filled && this.dirty) return 'valid';
      if (this.dirty) return 'dirty';

      return 'idle';
    },
    placeholderSet() {
      if (this.type === 'date') return 'mm/dd/yyyy';

      return this.placeholder || undefined;
    },
    typeSet() {
      if (this.type === 'date' && !this.browserSupportsInputDate) return 'text';

      return this.type;
    },
    acceptSet() {
      if (!this.accept || this.typeSet !== 'file') return false;

      const accept =
        typeof this.accept === 'string' ? [this.accept] : this.accept;

      const typeMap = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
      };

      return accept.map(type => typeMap[type] || type);
    },
    formState() {
      if (!this.parentId) return false;

      return (this.getForm(this.parentId) || {}).state;
    },
    telPrefix() {
      return this.getFieldTelPrefix(this.storeId);
    },
  },

  // Methods
  methods: {
    ...mapActions('forms', [
      'setupField',
      'updateField',
      'destroyField',
      'setFieldDirty',
      'setTelPrefix',
      'saveFile',
      'removeFile',
      'clearFiles',
    ]),

    handleTimezoneSelect(tz) {
      this.timezone = tz;
      localStorage.setItem('timezone', tz);
      this.openTimezone = false;
    },

    onChange(e) {
      let value = e.target.value;

      if (this.typeSet === 'date') {
        const updatedValue = moment(value, 'YYYY-MM-DD').format('MM/DD/YYYY');

        this.updateValue(updatedValue);

        return;
      }

      if (this.toggles !== null) {
        if (this.fieldType === 'boolean') value = e.target.checked;
        else if (this.fieldType === 'checkbox') {
          value = {
            ...this.fieldModel,
          };
          value[e.target.value] = e.target.checked;
        }

        this.updateValue(value);
        this.setFieldDirty(this.storeId);

        return;
      }

      if (this.typeSet === 'file') {
        const input = this.$refs.input;
        const files = input.files || [];
        value = files[0];
        this.updateValue(value);
        this.saveFile(value);
        this.removeValue();
      }
    },

    onInput(e) {
      let value = e.target.value;
      const input = this.$refs.input;

      if (e.target.id === 'org_location')
        localStorage.setItem('childOrg', value);

      if (this.typeSet === 'number' && e.data === 'e') {
        input.value = this.fieldModel;

        return false;
      }

      if (this.typeSet === 'file' && input) {
        const files = input.files || [];

        value = files[0];

        const validTypes = {
          'image/png': 'img',
          'image/jpeg': 'img',
          'application/pdf': 'iframe',
        };

        const previewIs = validTypes[value.type];

        if (previewIs) {
          const reader = new FileReader();

          reader.onload = e => {
            let src = e.target.result;

            if (previewIs === 'iframe') {
              src = URL.createObjectURL(value);
            }

            const preview = {
              src,
              is: previewIs,
            };

            this.$set(this, 'previewData', preview);
          };

          reader.readAsDataURL(value);
        }
      }

      this.updateValue(value);
    },
    onFocus() {
      this.focus = true;
    },
    onBlur() {
      this.focus = false;

      this.setFieldDirty(this.storeId);
    },
    onInputTelPrefix() {
      // WHY: Triggers an update of the field for validation purposes
      // this.updateValue(this.fieldModel);
    },
    updateValue(value) {
      this.updateField({
        id: this.storeId,
        value,
      });
    },
    removeValue() {
      this.updateValue(undefined);

      const input = this.$refs.input;

      if (this.typeSet === 'file' && input) {
        input.value = null;

        this.$set(this, 'previewData', {
          src: '',
        });
      }
    },
    createField() {
      const telPrefix = undefined;
      const value = this.value;

      this.setupField({
        id: this.storeId,
        type: this.type,
        required: this.required,
        multiple: this.multiple,
        options: this.options,
        value,
        min: this.minSet,
        telPrefix,
        max: this.max,
        matches: this.matches,
      });
    },
    onHighlightError() {
      this.highlightError = true;

      setTimeout(() => (this.highlightError = false), 1000); // WHY: Length of time in the animation CSS
    },
  },

  // Lifecycle
  created() {
    if (this.id.indexOf(this.separator) > -1) {
      console.warn(`Fieldset ID (${this.id}) cannot contain ${this.separator}`);
    }

    let parentId = 'global';

    const checkParent = node => {
      const nodeVnode = node.$vnode || {};
      const nodeComponentOptions = nodeVnode.componentOptions || {};
      const nodeComponentTag = nodeComponentOptions.tag || '';

      if (nodeComponentTag === 'module-form') return node;

      return node.$parent ? checkParent(node.$parent) : null;
    };

    if (this.$parent) {
      const form = checkParent(this.$parent) || {};

      if (form.id) {
        parentId = form.id;
      }
    }

    this.parentId = parentId;
  },
  mounted() {
    this.createField();

    this.$el.addEventListener(
      'highlight-error-fieldset',
      this.onHighlightError
    );

    const view = this.fieldView;
    const value = this.value;

    const fieldType = this.fieldType;

    let hasView = typeof view !== 'undefined';
    const hasValue = typeof value !== 'undefined';

    if (fieldType === 'select' && view === '') {
      hasView = false; // WHY: Fixes an issue where sometimes fields with no value were inheriting an empty string
    }

    const hasViewOrValue = hasView || hasValue;

    let valueToSet = hasView ? view : value;

    const el = this.$refs.input;

    if (this.type === 'tel' && this.fieldModel) {
      valueToSet = this.fieldModel;
    }

    if (el) {
      if (fieldType === 'boolean' && hasViewOrValue && el[0])
        el[0].checked = true;
      else if (fieldType === 'radio' && hasViewOrValue && el[valueToSet])
        el[valueToSet].checked = true;
      else if (fieldType === 'select')
        el.value = hasViewOrValue ? valueToSet : '-1';
      else if (fieldType === 'checkbox')
        arrayLoop(value || [], (k, v) => (el[k].checked = v));
      else if (hasViewOrValue) el.value = valueToSet;
    }
  },
  beforeDestroy() {
    this.$el.removeEventListener(
      'highlight-error-fieldset',
      this.onHighlightError
    );
  },
  destroyed() {
    this.destroyField(this.storeId);

    if (this.storeId === 'global|files') {
      this.clearFiles();
    }
  },
};
</script>



// WEBPACK FOOTER //
// Fieldset.vue?41f7729f